// src/utils/componentsMap.ts
// ─────────────────────────────────────────────────────────────────────────────
// SINGLE registry for all route components.
// - All imports are lazy → code split per route automatically
// - Never import pages directly in App.tsx or routeDefinitions.tsx
// - To add a new page: add one line here, use it everywhere else
// ─────────────────────────────────────────────────────────────────────────────
import { lazy } from 'react';

// ── Admin ─────────────────────────────────────────────────────────────────────
export const AdminDashboard       = lazy(() => import('../Admin/Dashboard/AdminDashboard'));
export const TrackingDashboard    = lazy(() => import('../Admin/Dashboard/TrackingDashboard'));
export const ApplyPageApplicationsDashboard = lazy(() => import('../Admin/Dashboard/ApplyPageApplicationsDashboard'));
export const ErrorEventsDashboard = lazy(() => import('../Admin/Dashboard/ErrorEventsDashboard'));
export const ExecEdgeOutreachEmailMonitor = lazy(() => import('../Admin/Dashboard/ExecEdgeOutreachEmailMonitor'));
export const AutomateEmailsDashboard = lazy(() => import('../Admin/Dashboard/AutomateEmailsDashboard'));
export const FeedbackSubmissionsDashboard = lazy(() => import('../Admin/Dashboard/FeedbackSubmissionsDashboard'));
export const ShortLinksDashboard  = lazy(() => import('../Admin/ShortLinks/ShortLinksDashboard'));
export const AdminPrivacyPage     = lazy(() => import('../Admin/Privacy/AdminPrivacyPage'));
export const AdminUserManagement  = lazy(() => import('../Admin/AdminUserManagement'));
export const NormalizationDashboard = lazy(() => import('../Admin/Database/NormalizationDashboard'));
export const MasterDataDashboard    = lazy(() => import('../Admin/Database/MasterDataDashboard'));
export const CandidateCorrectionDashboard = lazy(() => import('../Admin/Database/CandidateCorrectionDashboard'));
export const TypesenseDashboard   = lazy(() => import('../Admin/Database/TypesenseDashboard'));
export const AdminStatusManager   = lazy(() => import('../Admin/Status/AdminStatusManager'));
export const EmailReaderDashboard = lazy(() => import('../Admin/EmailReaderDashboard'));
export const SnapshotsList        = lazy(() => import('../Admin/Snapshots/SnapshotsList'));
export const CompanyHome          = lazy(() => import('../Admin/Company/CompanyHome'));
export const CompanyPeoplePage    = lazy(() => import('../Admin/People/CompanyPeoplePage'));
export const SignalsDashboard     = lazy(() => import('../Admin/Signals/SignalsDashboard'));
export const JobsDashboard        = lazy(() => import('../Admin/Jobs/JobsDashboard'));
export const WhatsAppDashboardPage = lazy(() => import('../Admin/WhatsApp/pages/WhatsAppDashboardPage'));
export const WhatsAppConversationsPage = lazy(() => import('../Admin/WhatsApp/pages/WhatsAppConversationsPage'));
export const WhatsAppConversationDetailPage = lazy(() => import('../Admin/WhatsApp/pages/WhatsAppConversationDetailPage'));
export const WhatsAppContactsPage = lazy(() => import('../Admin/WhatsApp/pages/WhatsAppContactsPage'));
export const WhatsAppContactDetailPage = lazy(() => import('../Admin/WhatsApp/pages/WhatsAppContactDetailPage'));
export const WhatsAppTemplatesPage = lazy(() => import('../Admin/WhatsApp/pages/WhatsAppTemplatesPage'));
export const WhatsAppQueuePage = lazy(() => import('../Admin/WhatsApp/pages/WhatsAppQueuePage'));
export const WhatsAppEventsPage = lazy(() => import('../Admin/WhatsApp/pages/WhatsAppEventsPage'));
export const WhatsAppHealthPage = lazy(() => import('../Admin/WhatsApp/pages/WhatsAppHealthPage'));
export const WorkersDashboard      = lazy(() => import('../Admin/Workers/WorkersDashboard'));
export const Ec2MonitoringPage     = lazy(() => import('../Admin/Infra/Ec2MonitoringPage'));
export const WorkerDetail          = lazy(() => import('../Admin/Workers/WorkerDetail'));
export const WorkerControlCenter   = lazy(() => import('../Admin/WorkerControlCenter'));
export const CandidateEditPage     = lazy(() => import('../Admin/Candidates/CandidateEditPage'));
export const MastersBrowser       = lazy(() => import('../Admin/Masters/MastersBrowser'));
export const RoleSearchLab        = lazy(() => import('../Admin/RoleSearch/RoleSearchLab'));

// ── Auth / misc ───────────────────────────────────────────────────────────────
export const Login            = lazy(() => import('../Login'));
export const GoogleCallback   = lazy(() => import('../GoogleCallback'));
export const OAuthCallback    = lazy(() => import('../OAuthCallback'));
export const TermsAndConditionsPage = lazy(() => import('../TermsAndConditionsPage'));
export const PrivacyDashboard = lazy(() => import('../Privacy/PrivacyDashboard'));
export const LegalAcceptPage = lazy(() => import('../Privacy/LegalAcceptPage'));
export const LegalDocumentPage = lazy(() => import('../Privacy/LegalDocumentPage'));
export const AccountDataPage = lazy(() => import('../Privacy/AccountDataPage'));

// ── Candidate / home ──────────────────────────────────────────────────────────
export const CandidateDashboardMain      = lazy(() => import('../Home/CandidateDashboard'));
export const CandidateHome               = lazy(() => import('../Home/CandidateHome'));
export const CandidateApplicationsPage   = lazy(() => import('../Home/CandidateApplicationsPage'));
export const TargetCompaniesPage         = lazy(() => import('../ExecEdge/NewExecedge/pages/TargetCompaniesPage'));
export const OutreachTrackerPage         = lazy(() => import('../ExecEdge/NewExecedge/outreach/OutreachTrackerPage'));
export const ExecEdgeClientsOpsPage      = lazy(() => import('../ExecEdge/NewExecedge/outreach/ExecEdgeClientsOpsPage'));
// ── Jobs ──────────────────────────────────────────────────────────────────────
export const JobPage   = lazy(() => import('../Jobs/JobPage'));
export const ApplyPage = lazy(() => import('../Jobs/ApplyPage'));
export const JoinWhatsAppPage = lazy(() => import('../pages/JoinWhatsAppPage'));
export const ShortLinkRedirect = lazy(() => import('../pages/ShortLinkRedirect'));

// ── HV / tracker ─────────────────────────────────────────────────────────────
export const ClientDashboard = lazy(() => import('../HV/Search/ClientDashboard'));
export const TypesenseCandidateSearch = lazy(() => import('../HV/Search/TypesenseCandidateSearch'));
export const OpenSearchCandidateSearch = lazy(() => import('../HV/Search/OpenSearchCandidateSearch'));
export const Associations    = lazy(() => import('../HV/Tracker/Association'));
export const RoleSanitize    = lazy(() => import('../HV/Role/RoleSanitize'));
export const CRMCompanies    = lazy(() => import('../Crm/CRMCompanies'));
export const HireVedaSheet   = lazy(() => import('../HV/Sheet/HireVedaSheet'));
export const RolePipelineReport = lazy(() => import('../HV/Reports/RolePipelineReport'));

// ── ExecEdge internal ─────────────────────────────────────────────────────────
export const Execedge                = lazy(() => import('../ExecEdge/Dashboard/dashboard'));
export const ExecedgeEntryRedirect   = lazy(() => import('../ExecEdge/ExecedgeEntryRedirect'));
export const ExecedgeInterviewBriefRedirect = lazy(() => import('../ExecEdge/ExecedgeInterviewBriefRedirect'));
export const ExecedgeLegacyInterviewBriefEntry = lazy(() => import('../ExecEdge/ExecedgeLegacyInterviewBriefEntry'));
export const ExecedgeNewInterviewBriefEntry = lazy(() => import('../ExecEdge/ExecedgeNewInterviewBriefEntry'));
export const InterviewBriefDashboard = lazy(() => import('../ExecEdge/InterviewBriefDashboard'));
export const CompensationBriefDashboard = lazy(() => import('../ExecEdge/CompensationBriefDashboard'));
export const ExecEdgeClientOpsHub = lazy(() => import('../ExecEdge/ExecEdgeClientOpsHub'));
export const ExecEdgeTrackerCandidatesDashboard = lazy(() => import('../ExecEdge/ExecEdgeTrackerCandidatesDashboard'));
export const ExecEdgePipelineReport = lazy(() => import('../ExecEdge/Reports/ExecEdgePipelineReport'));
export const EmailValidationDashboard = lazy(() => import('../ExecEdge/EmailValidation/EmailValidationDashboard'));
export const CompanyEmailsDashboard = lazy(() => import('../ExecEdge/CompanyEmails/CompanyEmailsDashboard'));
export const CompanyUploaderSheet = lazy(() => import('../ExecEdge/CompanyUploader/CompanyUploaderSheet'));
export const DmsUploaderSheet = lazy(() => import('../ExecEdge/DmsUploader/DmsUploaderSheet'));
export const ExecEdgeUploader = lazy(() => import('../ExecEdge/Uploader/ExecEdgeUploader'));
export const AllJobsPage = lazy(() => import('../ExecEdge/AllJobs/AllJobsPage'));
export const ExecEdgeDashboardTable  = lazy(() => import('../ExecEdge/Client/ExecEdgeDashboardTable'));
export const ExecEdgeFunnelDashboard = lazy(() => import('../pages/ExecEdge-Funnel/ExecEdgeFunnelDashboard'));
export const ExecEdgeCommsDashboard  = lazy(() => import('../pages/ExecEdge-Funnel/ExecEdgeCommsDashboard'));

// ── ExecEdge funnel (public) ──────────────────────────────────────────────────
export const ExecEdgeLandingPage     = lazy(() => import('../pages/ExecEdge-Funnel/ExecEdgeLanding'));
export const ExecEdgePreDemo          = lazy(() => import('../pages/ExecEdge-Funnel/predemo'));
export const ExecEdgeThankYou         = lazy(() => import('../pages/ExecEdge-Funnel/ThankYou'));
export const ExecEdgeDemoPlayer       = lazy(() => import('../pages/ExecEdge-Funnel/DemoPlayer'));
export const ExecEdgeProgramPlan      = lazy(() => import('../pages/ExecEdge-Funnel/ProgramPlan'));
export const ExecEdgeProgramPlanTest  = lazy(() => import('../pages/ExecEdge-Funnel/ProgramPlanTest'));
export const ExecEdgeFAQs             = lazy(() => import('../pages/ExecEdge-Funnel/FAQs'));
export const ExecEdgeDemoFeedback     = lazy(() => import('../pages/ExecEdge-Funnel/DemoFeedback'));
export const ExecEdgeFunnelSuccess    = lazy(() => import('../pages/ExecEdge-Funnel/Success'));
export const ExecEdgeProfileReviewPending = lazy(() => import('../pages/ExecEdge-Funnel/ProfileReviewPending'));
export const ExecEdgeProfileReview    = lazy(() => import('../pages/ExecEdge-Funnel/ProfileReview'));
export const ExecEdgeFlow             = lazy(() => import('../pages/ExecEdge-Funnel/funnel'));

// ── Onboarding ────────────────────────────────────────────────────────────────
export const OnboardingRouter = lazy(() => import('../ExecEdge-Onboarding/OnboardingRouter'));

// ── Resume builder ────────────────────────────────────────────────────────────
export const ResumeBuilderRouter = lazy(() => import('../ResumeBuilder/ResumeBuilderRouter'));

// ── Payments ──────────────────────────────────────────────────────────────────
export const RazorpayPayment  = lazy(() => import('./RazorpayPayment'));
export const PaymentDashboard = lazy(() => import('./PaymentDashboard'));
export const InvoiceList = lazy(() => import('../pages/Invoices/InvoiceList'));
export const InvoiceEditor = lazy(() => import('../pages/Invoices/InvoiceEditor'));

// ── Search ────────────────────────────────────────────────────────────────────
export const SearchPage = lazy(() => import('../pages/SearchPage'));
export const CrustdataSearch = lazy(() => import('../HV/Crustdata/CrustdataSearch'));

// ── AI flow ───────────────────────────────────────────────────────────────────
export const AiFlowRouter = lazy(() => import('../Ai-flow/AiFlowRouter'));

// ─────────────────────────────────────────────────────────────────────────────
// String → component map for config-driven routes (routeConfig.jsx)
// renderRoute() in routeDefinitions.tsx looks up components by string name.
// Keep this in sync with the named exports above.
// ─────────────────────────────────────────────────────────────────────────────
const componentsMap: Record<string, React.LazyExoticComponent<any>> = {
  // Admin
  AdminDashboard,
  TrackingDashboard,
  ApplyPageApplicationsDashboard,
  ErrorEventsDashboard,
  ExecEdgeOutreachEmailMonitor,
  AutomateEmailsDashboard,
  FeedbackSubmissionsDashboard,
  ShortLinksDashboard,
  AdminPrivacyPage,
  AdminUserManagement,
  NormalizationDashboard,
  MasterDataDashboard,
  CandidateCorrectionDashboard,
  TypesenseDashboard,
  AdminStatusManager,
  EmailReaderDashboard,
  SnapshotsList,
  CompanyHome,
  CompanyPeoplePage,
  SignalsDashboard,
  JobsDashboard,
  WhatsAppDashboardPage,
  WhatsAppConversationsPage,
  WhatsAppConversationDetailPage,
  WhatsAppContactsPage,
  WhatsAppContactDetailPage,
  WhatsAppTemplatesPage,
  WhatsAppQueuePage,
  WhatsAppEventsPage,
  WhatsAppHealthPage,
  WorkersDashboard,
  Ec2MonitoringPage,
  WorkerDetail,
  WorkerControlCenter,
  CandidateEditPage,
  MastersBrowser,
  RoleSearchLab,

  // Auth
  Login,
  GoogleCallback,
  OAuthCallback,
  TermsAndConditionsPage,
  PrivacyDashboard,
  LegalAcceptPage,
  LegalDocumentPage,
  AccountDataPage,

  // Candidate
  CandidateDashboardMain,
  CandidateHome,
  CandidateApplicationsPage,
  TargetCompaniesPage,
  OutreachTrackerPage,
  ExecEdgeClientsOpsPage,

  // Jobs
  JobPage,
  ApplyPage,

  // HV
  ClientDashboard,
  TypesenseCandidateSearch,
  OpenSearchCandidateSearch,
  Associations,
  RoleSanitize,
  CRMCompanies,
  HireVedaSheet,
  RolePipelineReport,

  // ExecEdge
  Execedge,
  ExecedgeEntryRedirect,
  ExecedgeInterviewBriefRedirect,
  ExecedgeLegacyInterviewBriefEntry,
  ExecedgeNewInterviewBriefEntry,
  InterviewBriefDashboard,
  CompensationBriefDashboard,
  ExecEdgeClientOpsHub,
  ExecEdgeTrackerCandidatesDashboard,
  ExecEdgePipelineReport,
  EmailValidationDashboard,
  CompanyEmailsDashboard,
  CompanyUploaderSheet,
  DmsUploaderSheet,
  ExecEdgeUploader,
  AllJobsPage,
  ExecEdgeDashboardTable,
  ExecEdgeFunnelDashboard,
  ExecEdgeCommsDashboard,

  // Onboarding
  OnboardingRouter,

  // Resume
  ResumeBuilderRouter,

  // Payments
  RazorpayPayment,
  PaymentDashboard,
  InvoiceList,
  InvoiceEditor,

  // Search
  SearchPage,
  CrustdataSearch,

  // AI
  AiFlowRouter,
};

export default componentsMap;
